import { EllipsisVertical } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { useBulkReachOutCandidateJobMutation } from '@/modules/candidates/mutations/useBulkReachOutCandidateJobMutation';
import { useContactCandidateJobMutation } from '@/modules/candidates/mutations/useContactCandidateJobMutation';
import { useCreateCandidateFeedbackMutation } from '@/modules/candidates/mutations/useCreateCandidateFeedbackMutation';
import { useDeclineCandidateJobMutation } from '@/modules/candidates/mutations/useDeclineCandidateJobMutation';
import { useGeneratePrescreenLinkMutation } from '@/modules/candidates/mutations/useGeneratePrescreenLinkMutation';
import { useHireCandidateJobMutation } from '@/modules/candidates/mutations/useHireCandidateJobMutation';
import { useInterviewCandidateJobMutation } from '@/modules/candidates/mutations/useInterviewCandidateJobMutation';
import { usePrescreenCandidateJobMutation } from '@/modules/candidates/mutations/usePrescreenCandidateJobMutation';
import { useRejectCandidateJobMutation } from '@/modules/candidates/mutations/useRejectCandidateJobMutation';
import { useShortlistCandidateJobMutation } from '@/modules/candidates/mutations/useShortlistCandidateJobMutation';
import { useSubmitCandidateJobMutation } from '@/modules/candidates/mutations/useSubmitCandidateJobMutation';
import type { CandidateJobApi } from '@/modules/candidates/types';
import type { JobApi } from '@/modules/jobs/types';
import { getMessageLink } from '@/modules/messaging/utils/getMessageLink';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { CandidateAnnotationDialog } from './CandidateAnnotationDialog';
import { GatedReachOutButton } from './GatedReachOutButton';

interface DropdownItem {
  label: string;
  key: string;
  onClick: () => void;
  disabled?: boolean;
}

interface CandidateActionButtonProps {
  candidateJob: CandidateJobApi;
  /** Only needed to gate the Approve/Reject buttons to Review Candidates jobs; omit where unavailable. */
  job?: Pick<JobApi, 'submissionDecisionMode'>;
  displayedAssessmentRevisionId?: string | null;
}

export const CandidateActionButton = ({
  candidateJob,
  job,
  displayedAssessmentRevisionId,
}: CandidateActionButtonProps) => {
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    'reject' | 'feedbackPositive' | 'feedbackNegative' | null
  >(null);
  const [feedbackRevisionId, setFeedbackRevisionId] = useState<string | null>(
    null,
  );

  const shortlistCandidateJobMutation = useShortlistCandidateJobMutation();
  const submitCandidateJobMutation = useSubmitCandidateJobMutation();
  const contactCandidateJobMutation = useContactCandidateJobMutation();
  const rejectCandidateJobMutation = useRejectCandidateJobMutation();
  const declineCandidateJobMutation = useDeclineCandidateJobMutation();
  const generatePrescreenLinkMutation = useGeneratePrescreenLinkMutation();
  const prescreenCandidateJobMutation = usePrescreenCandidateJobMutation();
  const interviewCandidateJobMutation = useInterviewCandidateJobMutation();
  const hireCandidateJobMutation = useHireCandidateJobMutation();
  const reachOutCandidatesJobMutation = useBulkReachOutCandidateJobMutation();
  const createFeedbackMutation = useCreateCandidateFeedbackMutation(
    candidateJob.jobId,
    candidateJob.candidate.id,
  );
  const hasUserPermission = useCheckPermissions();
  const currentUserQuery = useCurrentUserQuery();
  const currentFeedbackRevisionId =
    displayedAssessmentRevisionId === undefined
      ? candidateJob.assessment.displayedRevisionId
      : displayedAssessmentRevisionId;

  const openAnnotationDialog = (
    action: 'reject' | 'feedbackPositive' | 'feedbackNegative',
  ) => {
    setFeedbackRevisionId(currentFeedbackRevisionId);
    setPendingAction(action);
    setAnnotationDialogOpen(true);
  };

  const handleGeneratePrescreenLink = () => {
    const prescreenWindow = window.open('', '_blank');
    if (prescreenWindow) {
      prescreenWindow.opener = null;
    }

    generatePrescreenLinkMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onSuccess: (response) => {
          if (!response) {
            prescreenWindow?.close();
            toast.error('Pre-screen link could not be generated.');
            return;
          }

          const { url } = response;

          if (prescreenWindow && !prescreenWindow.closed) {
            prescreenWindow.location.assign(url);
            return;
          }

          window.location.assign(url);
        },
        onError: (error) => {
          prescreenWindow?.close();
          toast.error(
            `Pre-screen link could not be generated: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleShortlist = () => {
    shortlistCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been shortlisted: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been shortlisted`,
          );
        },
      },
    );
  };

  const handleSubmit = () => {
    submitCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been submitted: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been submitted`,
          );
        },
      },
    );
  };

  const handleReject = () => {
    openAnnotationDialog('reject');
  };

  const handleDecline = () => {
    declineCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been declined: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been declined`,
          );
        },
      },
    );
  };

  const handleFeedbackPositive = () => {
    openAnnotationDialog('feedbackPositive');
  };

  const handleFeedbackNegative = () => {
    openAnnotationDialog('feedbackNegative');
  };

  const handleAnnotationConfirm = (
    annotation: string | null,
    rejectionMessage: string | null,
  ) => {
    setAnnotationDialogOpen(false);
    const isPositive = pendingAction === 'feedbackPositive';
    const fullName = `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName}`;
    const params = {
      jobId: candidateJob.jobId,
      candidateId: candidateJob.candidate.id,
    };

    if (pendingAction === 'reject') {
      rejectCandidateJobMutation.mutate(
        { ...params, message: rejectionMessage },
        {
          onError: (error) => {
            toast.error(
              `${fullName} has not been rejected: ${extractErrorMessage(error)}`,
            );
          },
          onSuccess: () => {
            toast.success(`${fullName} has been rejected`);
            if (annotation) {
              createFeedbackMutation.mutate({
                isPositive,
                annotation,
                assessmentLedgerRevisionId: feedbackRevisionId,
              });
            }
          },
        },
      );
    } else if (
      pendingAction === 'feedbackPositive' ||
      pendingAction === 'feedbackNegative'
    ) {
      createFeedbackMutation.mutate(
        {
          isPositive,
          annotation,
          assessmentLedgerRevisionId: feedbackRevisionId,
        },
        {
          onError: (error) => {
            toast.error(
              `Feedback has not been submitted: ${extractErrorMessage(error)}`,
            );
          },
          onSuccess: () => {
            toast.success(`Feedback for ${fullName} has been submitted`);
          },
        },
      );
    }

    setPendingAction(null);
    setFeedbackRevisionId(null);
  };

  const handleContacted = () => {
    contactCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been moved to contacted: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been moved to contacted`,
          );
        },
      },
    );
  };

  const handleInterview = () => {
    interviewCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been moved to interviewing: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been moved to interviewing`,
          );
        },
      },
    );
  };

  const handlePrescreened = () => {
    prescreenCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been moved to prescreened: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been moved to prescreened`,
          );
        },
      },
    );
  };

  const handleHire = () => {
    hireCandidateJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateId: candidateJob.candidate.id },
      {
        onError: (error) => {
          toast.error(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been hired: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been hired`,
          );
        },
      },
    );
  };

  const handleReachOut = () => {
    reachOutCandidatesJobMutation.mutate(
      { jobId: candidateJob.jobId, candidateIds: [candidateJob.candidate.id] },
      {
        onSuccess: () => {
          toast.success(
            `The draft message for ${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has been created`,
          );
        },
        onError: (error) => {
          toast.error(
            `The draft message for ${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName} has not been created: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const currentUserId = currentUserQuery.data?.id;
  const hasCurrentUserProvidedFeedback =
    currentUserId !== undefined &&
    ([
      ...(candidateJob.feedbacks?.hmFeedbacks ?? []),
      ...(candidateJob.feedbacks?.recruiterFeedbacks ?? []),
    ].some((f) => f.author?.id === currentUserId) ??
      false);

  const hasUserCandidateJobsWritePermission = hasUserPermission([
    permissions.CandidateJobs.Write,
  ]);
  const shortlistItem = hasUserCandidateJobsWritePermission
    ? {
        label: 'Shortlist',
        key: 'shortlist',
        onClick: handleShortlist,
      }
    : null;

  const submitItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Submit',
        key: 'submit',
        onClick: handleSubmit,
      }
    : null;

  const rejectItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Reject',
        key: 'reject',
        onClick: handleReject,
      }
    : null;

  const declineItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Decline',
        key: 'decline',
        onClick: handleDecline,
      }
    : null;

  const positiveFeedbackItem: DropdownItem | null =
    hasUserPermission([permissions.CandidateFeedbacks.Write]) &&
    !hasCurrentUserProvidedFeedback
      ? {
          label: 'Positive feedback',
          key: 'feedbackPositive',
          onClick: handleFeedbackPositive,
        }
      : null;

  const negativeFeedbackItem: DropdownItem | null =
    hasUserPermission([permissions.CandidateFeedbacks.Write]) &&
    !hasCurrentUserProvidedFeedback
      ? {
          label: 'Negative feedback',
          key: 'feedbackNegative',
          onClick: handleFeedbackNegative,
        }
      : null;

  const prescreenItem: DropdownItem | null =
    candidateJob.status === 'Contacted'
      ? {
          label: generatePrescreenLinkMutation.isPending
            ? 'Generating pre-screen link...'
            : 'Generate pre-screen link',
          key: 'prescreen',
          onClick: handleGeneratePrescreenLink,
          disabled: generatePrescreenLinkMutation.isPending,
        }
      : null;

  const markPrescreenedItem: DropdownItem | null =
    hasUserCandidateJobsWritePermission
      ? {
          label: 'Mark as Prescreened',
          key: 'prescreened',
          onClick: handlePrescreened,
        }
      : null;

  const reachOutItem: DropdownItem | null = hasUserPermission([
    permissions.Messaging.Write,
  ])
    ? {
        label: 'Reach out',
        key: 'reachOut',
        onClick: handleReachOut,
      }
    : null;

  const contactItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Mark as Contacted',
        key: 'contacted',
        onClick: handleContacted,
      }
    : null;

  const interviewItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Move to Interviewing',
        key: 'interview',
        onClick: handleInterview,
      }
    : null;

  const hireItem: DropdownItem | null = hasUserCandidateJobsWritePermission
    ? {
        label: 'Move to Hired',
        key: 'hire',
        onClick: handleHire,
      }
    : null;

  // does not contain all the possible transitions, because some happen only automatically
  // for example -> "contacted"
  const transitionItemMap: Partial<Record<string, DropdownItem | null>> = {
    Shortlisted: shortlistItem,
    Submitted: submitItem,
    Prescreened: markPrescreenedItem,
    Rejected: rejectItem,
    Declined: declineItem,
    ContactStarted: reachOutItem,
    Interviewing: interviewItem,
    Hired: hireItem,
  };

  const getDropdownItems = (candidateJob: CandidateJobApi): DropdownItem[] => {
    const transitionItems = (candidateJob.allowedTransitions ?? [])
      .map((transition) => transitionItemMap[transition] ?? null)
      .filter((item): item is DropdownItem => item !== null);

    // a workaround to support recruiter override to "contacted"
    const customItems =
      candidateJob.status === 'New' ||
      candidateJob.status === 'Shortlisted' ||
      candidateJob.status === 'ContactStarted'
        ? [contactItem]
        : [];

    return [
      ...transitionItems,
      ...customItems,
      positiveFeedbackItem,
      negativeFeedbackItem,
      prescreenItem,
    ].filter((item): item is DropdownItem => item !== null);
  };

  const isMessageCountVisible =
    candidateJob.messaging?.messageCount &&
    candidateJob.messaging.messageCount > 0;

  const messagingLink = getMessageLink({
    threadId: candidateJob.messaging?.messageThreadId,
    messageId: candidateJob.messaging?.messageId,
    hasSentMessages: candidateJob.messaging?.hasSentMessages,
  });

  const dropdownItems = getDropdownItems(candidateJob);

  const isAnnotationLoading =
    rejectCandidateJobMutation.isPending || createFeedbackMutation.isPending;

  return (
    <>
      <CandidateAnnotationDialog
        open={annotationDialogOpen}
        onOpenChange={setAnnotationDialogOpen}
        isPositive={pendingAction === 'feedbackPositive'}
        fullName={`${candidateJob.candidate.firstName} ${candidateJob.candidate.lastName}`}
        rejection={
          pendingAction === 'reject'
            ? {
                jobId: candidateJob.jobId,
                candidateId: candidateJob.candidate.id,
                firstName: candidateJob.candidate.firstName,
              }
            : undefined
        }
        onConfirm={handleAnnotationConfirm}
        isLoading={isAnnotationLoading}
      />
      {isMessageCountVisible ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.Messaging.Read]}>
            <div className="relative">
              <Button variant="outline" asChild>
                <Link to={messagingLink}>View messages</Link>
              </Button>
              <Badge className="absolute -top-2 -right-2 flex size-5 items-center justify-center p-0 text-[10px]">
                {candidateJob.messaging?.messageCount}
              </Badge>
            </div>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'New' ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Button
              variant="primary"
              loading={shortlistCandidateJobMutation.isPending}
              onClick={handleShortlist}
            >
              Shortlist
            </Button>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'Shortlisted' ? (
        <ActionGroup>
          <GatedReachOutButton
            isPending={reachOutCandidatesJobMutation.isPending}
            onClick={handleReachOut}
          >
            Reach out
          </GatedReachOutButton>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="warning"
                  loading={contactCandidateJobMutation.isPending}
                  onClick={handleContacted}
                >
                  Mark as Contacted
                </Button>
              </TooltipTrigger>
              <TooltipContent
                variant="warning"
                side="bottom"
                className="max-w-xs text-left"
              >
                Move candidate to contacted stage in case the reach out happened
                outside of the platform.
              </TooltipContent>
            </Tooltip>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'ContactStarted' ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="warning"
                  loading={contactCandidateJobMutation.isPending}
                  onClick={handleContacted}
                >
                  Mark as Contacted
                </Button>
              </TooltipTrigger>
              <TooltipContent
                variant="warning"
                side="bottom"
                className="max-w-xs text-left"
              >
                Move candidate to contacted stage in case the reach out happened
                outside of the platform.
              </TooltipContent>
            </Tooltip>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'Contacted' ? (
        <ActionGroup>
          <Button
            variant="outline"
            loading={generatePrescreenLinkMutation.isPending}
            onClick={handleGeneratePrescreenLink}
          >
            Pre-screen
          </Button>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'AssessmentCompleted' &&
        job?.submissionDecisionMode === 'Human' ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Button
              variant="primary"
              loading={submitCandidateJobMutation.isPending}
              onClick={handleSubmit}
            >
              Approve
            </Button>
          </PermissionsGate>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Button
              variant="destructive"
              loading={rejectCandidateJobMutation.isPending}
              onClick={handleReject}
            >
              Reject
            </Button>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'Submitted' ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Button
              variant="primary"
              loading={interviewCandidateJobMutation.isPending}
              onClick={handleInterview}
            >
              Move to Interviewing
            </Button>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : candidateJob.status === 'Interviewing' ? (
        <ActionGroup>
          <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
            <Button
              variant="primary"
              loading={hireCandidateJobMutation.isPending}
              onClick={handleHire}
            >
              Move to Hired
            </Button>
          </PermissionsGate>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      ) : (
        <ActionGroup>
          <DropdownActionButton items={dropdownItems} />
        </ActionGroup>
      )}
    </>
  );
};

const DropdownActionButton = ({ items }: { items: DropdownItem[] }) => {
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-primary hover:text-primary cursor-pointer"
        >
          <EllipsisVertical size={24} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={item.onClick}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ActionGroup = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2">{children}</div>
);
